import React, { useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 1: Distributed Monitoring Platform
 * - Config-as-code (Bitbucket -> Sync Job -> Uptime Kuma)
 * - Monitoring Service Account (ECS, Uptime, DB, Storage, NAT)
 * - Private connectivity (TGW/VPN)
 * - Customer account (ILB, EC2, Auth, GraphQL, RDS, Mongo, CloudWatch, Alarms, SQS/Worker, Enduser app, VPC Endpoints)
 * - Logging to Splunk
 */

// ---- Visual styles (match your category colors)
const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 14,
    padding: 10,
    fontWeight: 800,
    color: "#0f172a",
    width: 320,
    textAlign: "center",
  },
  repo: {
    background: "#f8f0fc",
    border: "2px solid #7048e8",
    borderRadius: 12,
    padding: 14,
    fontWeight: 700,
    color: "#2b145a",
    width: 260,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 14,
    fontWeight: 700,
    color: "#0b2e59",
    width: 260,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 14,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 260,
    textAlign: "center",
  },
  obs: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 14,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 260,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 14,
    fontWeight: 700,
    color: "#05333b",
    width: 260,
    textAlign: "center",
  },
};

// ---- Helper to build edges consistently
function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project1FlowDiagram() {
  const nodes = useMemo(
    () => [
      // ---- Background containers (rendered first, behind other nodes)
      {
        id: "bg-config",
        position: { x: 750, y: 30 },
        data: { label: "" },
        style: {
          width: 340,
          height: 280,
          background: "#f8f0fc20",
          border: "3px solid #7048e8",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-monitoring",
        position: { x: 110, y: 370 },
        data: { label: "" },
        style: {
          width: 640,
          height: 430,
          background: "#e7f5ff20",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-customer",
        position: { x: 1210, y: 370 },
        data: { label: "" },
        style: {
          width: 680,
          height: 1000,
          background: "#f0fdf420",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-databases",
        position: { x: 1240, y: 570 },
        data: { label: "" },
        style: {
          width: 600,
          height: 290,
          background: "transparent",
          border: "3px dashed #a61e4d",
          borderRadius: 12,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // ---- CONFIG AS CODE (Top)
      {
        id: "sec-config",
        position: { x: 760, y: 40 },
        data: { label: "CONFIG AS CODE" },
        style: nodeStyle.section,
      },
      {
        id: "repo",
        position: { x: 790, y: 120 },
        data: { label: "Bitbucket Repo\nMonitor configs per customer" },
        style: nodeStyle.repo,
      },
      {
        id: "sync",
        position: { x: 790, y: 230 },
        data: { label: "Sync Job\nValidates & applies configs" },
        style: nodeStyle.repo,
      },

      // ---- MONITORING SERVICE ACCOUNT (Left)
      {
        id: "sec-svc",
        position: { x: 120, y: 380 },
        data: { label: "MONITORING SERVICE" },
        style: { ...nodeStyle.section, width: 420, borderColor: "#93c5fd" },
      },
      {
        id: "ecs",
        position: { x: 160, y: 470 },
        data: { label: "ECS Cluster" },
        style: nodeStyle.compute,
      },
      {
        id: "uptime",
        position: { x: 160, y: 590 },
        data: { label: "Uptime Kuma Services\none container customer-X" },
        style: nodeStyle.compute,
      },
      {
        id: "uptimeDb",
        position: { x: 460, y: 470 },
        data: { label: "Uptime DB\n SQLite" },
        style: nodeStyle.data,
      },
      {
        id: "storage",
        position: { x: 460, y: 590 },
        data: { label: "Persistent Storage\nEFS or EBS" },
        style: nodeStyle.data,
      },
      {
        id: "nat",
        position: { x: 310, y: 740 },
        data: { label: "NAT Gateway\ncontrolled outbound" },
        style: nodeStyle.net,
      },

      // ---- PRIVATE CONNECTIVITY (Center)
      {
        id: "tgw",
        position: { x: 780, y: 640 },
        data: { label: "Transit Gateway\nor Private VPN" },
        style: nodeStyle.net,
      },

      // ---- CUSTOMER WORKLOAD (Right)
      {
        id: "sec-cust",
        position: { x: 1220, y: 380 },
        data: { label: "CUSTOMER WORKLOAD" },
        style: { ...nodeStyle.section, width: 520, borderColor: "#86efac" },
      },
      {
        id: "ilb",
        position: { x: 1260, y: 470 },
        data: { label: "Internal Load Balancer\nprivate endpoints" },
        style: nodeStyle.compute,
      },
      {
        id: "apps",
        position: { x: 1260, y: 590 },
        data: { label: "EC2 web-app fleet\n ;API servers fleet\n ;Other servers" },
        style: nodeStyle.compute,
      },
      {
        id: "auth",
        position: { x: 1560, y: 470 },
        data: { label: "Auth services\nselective customers" },
        style: nodeStyle.compute,
      },
      {
        id: "gql",
        position: { x: 1560, y: 590 },
        data: { label: "API endpoints\nselective customers" },
        style: nodeStyle.compute,
      },
      {
        id: "rds",
        position: { x: 1260, y: 740 },
        data: { label: "RDS databases" },
        style: nodeStyle.data,
      },
      {
        id: "mongo",
        position: { x: 1560, y: 740 },
        data: { label: "MongoDB databases" },
        style: nodeStyle.data,
      },

      // Observability + Automation
      {
        id: "cw",
        position: { x: 1260, y: 900 },
        data: { label: "CloudWatch\nmetrics and logs" },
        style: nodeStyle.obs,
      },
      {
        id: "alarms",
        position: { x: 1560, y: 900 },
        data: { label: "CloudWatch Alarms" },
        style: nodeStyle.obs,
      },
      {
        id: "sqs",
        position: { x: 1410, y: 1030 },
        data: { label: "SQS Queue" },
        style: nodeStyle.obs,
      },
      {
        id: "worker",
        position: { x: 1410, y: 1160 },
        data: { label: "EC2 broker\nvalidates and toggles alarms" },
        style: nodeStyle.compute,
      },
      {
        id: "enduser",
        position: { x: 1410, y: 1300 },
        data: { label: "Enduser_notification_app" },
        style: nodeStyle.obs,
      },
      {
        id: "vpce",
        position: { x: 1410, y: 820 },
        data: { label: "VPC Endpoints\n" },
        style: nodeStyle.net,
      },

      // Central logging
      {
        id: "splunk",
        position: { x: 780, y: 1200 },
        data: { label: "Splunk\ncentral logging and audit" },
        style: nodeStyle.obs,
      },
    ],
    []
  );

  const edges = useMemo(() => {
    const purple = { stroke: "#7c3aed" };
    const teal = { stroke: "#0891b2" };
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const orange = { stroke: "#f59e0b" };
    const gray = { stroke: "#64748b" };

    return [
      // Config-as-code flow
      mkEdge("e-repo-sync", "repo", "sync", "git changes", {
        animated: true,
        style: { ...purple, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
      mkEdge("e-sync-uptime", "sync", "uptime", "apply monitors", {
        animated: true,
        style: { ...purple, strokeWidth: 5, strokeDasharray: "10 8" },
      }),

      // Service runtime dependencies
      mkEdge("e-ecs-uptime", "ecs", "uptime", "service runtime", {
        style: { stroke: "#64748b", strokeWidth: 4 },
      }),
      mkEdge("e-uptime-db", "uptime", "uptimeDb", "write state", {
        style: { stroke: "#a61e4d", strokeWidth: 4 },
      }),
      mkEdge("e-uptime-store", "uptime", "storage", "store history", {
        style: { stroke: "#a61e4d", strokeWidth: 4 },
      }),
      mkEdge("e-uptime-nat", "uptime", "nat", "egress", {
        style: { ...teal, strokeWidth: 4 },
      }),

      // Private routing via TGW/VPN
      mkEdge("e-svc-tgw", "uptime", "tgw", "private routing", {
        animated: true,
        style: { ...teal, strokeWidth: 5, strokeDasharray: "14 10" },
      }),

      // Synthetic checks (via TGW into customer VPC targets)
      mkEdge("e-tgw-ilb", "tgw", "ilb", "synthetic checks", {
        animated: true,
        style: { ...blue, strokeWidth: 5.5, strokeDasharray: "16 10" },
      }),
      mkEdge("e-tgw-apps", "tgw", "apps", "api checks", {
        animated: true,
        style: { ...blue, strokeWidth: 5.5, strokeDasharray: "16 10" },
      }),
      mkEdge("e-tgw-auth", "tgw", "auth", "auth checks", {
        animated: true,
        style: { ...blue, strokeWidth: 5.5, strokeDasharray: "16 10" },
      }),
      mkEdge("e-tgw-gql", "tgw", "gql", "graphql checks", {
        animated: true,
        style: { ...blue, strokeWidth: 5.5, strokeDasharray: "16 10" },
      }),

      // Customer app internal flows
      mkEdge("e-ilb-apps", "ilb", "apps", "route", {}),
      mkEdge("e-apps-rds", "apps", "rds", "sql", {}),
      mkEdge("e-apps-mongo", "apps", "mongo", "nosql", {}),

      // Observability
      mkEdge("e-apps-cw", "apps", "cw", "metrics and logs", { style: { ...green } }),
      mkEdge("e-db-cw", "bg-databases", "cw", "db metrics", { style: { ...green, strokeWidth: 4 } }),
      mkEdge("e-cw-alarms", "cw", "alarms", "evaluate thresholds", { style: { ...green } }),

      // Alarm automation pipeline
      mkEdge("e-alarms-sqs", "alarms", "sqs", "alarm events", {
        style: { ...green, strokeWidth: 5 },
      }),
      mkEdge("e-sqs-worker", "sqs", "worker", "consume", {
        style: { ...green, strokeWidth: 5 },
      }),
      mkEdge("e-worker-enduser", "worker", "enduser", "notify", {
        style: { ...orange, strokeWidth: 5 },
      }),

      // VPC endpoints note (link for context)
      mkEdge("e-vpce-cw", "vpce", "cw", "private aws api", {
        style: { ...teal, strokeWidth: 4, strokeDasharray: "6 8" },
      }),

      // Central logging
      mkEdge("e-uptime-splunk", "uptime", "splunk", "uptime events", {
        style: { ...gray, strokeWidth: 4, strokeDasharray: "3 9" },
      }),
      mkEdge("e-worker-splunk", "worker", "splunk", "alarm audit logs", {
        style: { ...gray, strokeWidth: 4, strokeDasharray: "3 9" },
      }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "900px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.3}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
